import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import { FlexRowBetween, TitleText } from 'components'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { ActionPlanCategoryService } from 'utility'
import { getApiErrorMessage, normalizeActionPlanCategory } from './utils'
import {
    FormHelper,
    InlineRow,
    ModalError,
    PolicyModalActions,
    SegBtn,
    Segmented,
} from 'page-components/escalation-policy/styles'

const emptyForm = () => ({
    name: '',
    isActive: true,
})

export const ActionPlanCategoryFormModal = ({
    open,
    onClose,
    category,
    onSaved,
}) => {
    const isEdit = Boolean(category?.id)
    const [form, setForm] = useState(emptyForm())
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open) return
        setError('')
        setConfirmDelete(false)
        if (category) {
            setForm({
                name: category.name || '',
                isActive: category.isActive !== false,
            })
        } else {
            setForm(emptyForm())
        }
    }, [open, category])

    const updateForm = patch => setForm(prev => ({ ...prev, ...patch }))

    const canSave = Boolean(form.name.trim())

    const handleSave = async () => {
        if (!canSave) return
        setSaving(true)
        setError('')
        try {
            if (isEdit) {
                const payload = {
                    CategoryName: form.name.trim(),
                    IsActive: Boolean(form.isActive),
                }
                await ActionPlanCategoryService.update(category.id, payload)
                onSaved?.(
                    normalizeActionPlanCategory({
                        CategoryId: category.id,
                        CategoryName: form.name.trim(),
                        IsActive: form.isActive,
                    }),
                )
            } else {
                const res = await ActionPlanCategoryService.create({
                    CategoryName: form.name.trim(),
                })
                const raw = res?.Data ?? res?.data ?? res ?? {}
                const saved = normalizeActionPlanCategory({
                    ...raw,
                    CategoryName:
                        raw.CategoryName ||
                        raw.categoryName ||
                        form.name.trim(),
                    IsActive: true,
                })
                if (!saved.id) {
                    saved.id =
                        raw.CategoryId ?? raw.categoryId ?? raw.Id ?? raw.id
                }
                if (!saved.name) saved.name = form.name.trim()
                onSaved?.(saved)
            }
            onClose?.()
        } catch (err) {
            setError(getApiErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    const softDeleteCategory = async () => {
        const categoryId = Number(category.id)
        try {
            await ActionPlanCategoryService.remove(categoryId)
        } catch (err) {
            if (err?.status !== 404) throw err
            await ActionPlanCategoryService.update(categoryId, {
                CategoryName: form.name.trim() || category.name,
                IsActive: false,
            })
        }
    }

    const handleDelete = async () => {
        if (!isEdit) return
        if (!form.isActive) {
            setError(
                'This category is already Inactive. Soft-delete only flips Status to INACTIVE and keeps rule linkages.',
            )
            return
        }
        if (!confirmDelete) {
            setConfirmDelete(true)
            return
        }
        setDeleting(true)
        setError('')
        try {
            await softDeleteCategory()
            onSaved?.({
                ...category,
                name: form.name.trim() || category.name,
                isActive: false,
                softDeleted: true,
            })
            onClose?.()
        } catch (err) {
            setError(getApiErrorMessage(err))
            setConfirmDelete(false)
        } finally {
            setDeleting(false)
        }
    }

    const title = isEdit ? 'Edit ticket category' : 'New ticket category'

    return (
        <Modal open={open} onClose={onClose} size="tiny">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{title}</TitleText>
                    <X className="pointer" size={28} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {error && <ModalError>{error}</ModalError>}
                <Form>
                    <Form.Field required>
                        <label>Category name</label>
                        <FormHelper>
                            Unique per company (case-insensitive). Used for
                            reporting on auto-ticket rules.
                        </FormHelper>
                        <Form.Input
                            value={form.name}
                            placeholder="e.g. Safety, Quality, Facilities"
                            maxLength={100}
                            onChange={(e, { value }) =>
                                updateForm({ name: value })
                            }
                        />
                    </Form.Field>
                    {isEdit && (
                        <Form.Field>
                            <label>Status</label>
                            <FormHelper>
                                Inactive categories stay on this list but are
                                hidden from the rule-editor dropdown.
                            </FormHelper>
                            <InlineRow style={{ marginTop: 8 }}>
                                <Segmented>
                                    <SegBtn
                                        type="button"
                                        $on={form.isActive}
                                        onClick={() =>
                                            updateForm({ isActive: true })
                                        }
                                    >
                                        Active
                                    </SegBtn>
                                    <SegBtn
                                        type="button"
                                        $on={!form.isActive}
                                        onClick={() =>
                                            updateForm({ isActive: false })
                                        }
                                    >
                                        Inactive
                                    </SegBtn>
                                </Segmented>
                            </InlineRow>
                            <FormHelper style={{ marginTop: 8 }}>
                                <b>Delete</b> is a soft-delete: it sets Status
                                to Inactive (same as the backend map). It does
                                not remove the row.
                            </FormHelper>
                        </Form.Field>
                    )}
                </Form>
            </Modal.Content>
            <PolicyModalActions>
                {isEdit && form.isActive && (
                    <AppSecondaryButton
                        onClick={handleDelete}
                        loading={deleting}
                        disabled={saving || deleting}
                        style={{ color: '#b91c1c' }}
                    >
                        {confirmDelete ? 'Confirm soft-delete?' : 'Delete'}
                    </AppSecondaryButton>
                )}
                <div style={{ flex: 1 }} />
                <AppSecondaryButton
                    onClick={onClose}
                    disabled={saving || deleting}
                >
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton
                    onClick={handleSave}
                    loading={saving}
                    disabled={!canSave || saving || deleting}
                >
                    {isEdit ? 'Save changes' : 'Create category'}
                </AppPrimaryButton>
            </PolicyModalActions>
        </Modal>
    )
}
